import { StaffPerformanceRecord, StaffSuccessUpdates, SuccessUpdateCounts, SuccessUpdatesMeta } from '../types';

/**
 * Joins the success-update documents onto the records by staff id. A coach with no document
 * leads no active clients, so they get `null` rather than being left "not loaded".
 */
export function attachSuccessUpdates(
  records: StaffPerformanceRecord[],
  byStaffId: Map<string, StaffSuccessUpdates>
): StaffPerformanceRecord[] {
  return records.map((record) => ({ ...record, success_updates: byStaffId.get(record.id) ?? null }));
}

/** What a scope with no active clients (or no document) adds up to. */
export const EMPTY_SUCCESS_COUNTS: Readonly<SuccessUpdateCounts> = { active: 0, overdue: 0, awaiting_first: 0, due_soon: 0, on_track: 0, not_yet_due: 0 };

/** Team totals over the records that have been checked; `null` when none have (so "not loaded" stays distinct from zero). */
export function sumSuccessUpdates(records: StaffPerformanceRecord[]): SuccessUpdateCounts | null {
  const checked = records.filter((record) => record.success_updates !== undefined);
  if (checked.length === 0) return null;

  return checked.reduce<SuccessUpdateCounts>((total, record) => {
    const counts = record.success_updates?.counts;
    if (!counts) return total;
    return {
      active: total.active + counts.active,
      overdue: total.overdue + counts.overdue,
      awaiting_first: total.awaiting_first + counts.awaiting_first,
      due_soon: total.due_soon + counts.due_soon,
      on_track: total.on_track + counts.on_track,
      not_yet_due: total.not_yet_due + counts.not_yet_due,
    };
  }, { ...EMPTY_SUCCESS_COUNTS });
}

/** Overdue clients as a whole-number percentage of active clients; `null` when there are no active clients. */
export function overdueShare(counts: SuccessUpdateCounts): number | null {
  return counts.active === 0 ? null : Math.round((counts.overdue / counts.active) * 100);
}

/**
 * Whether the client-update figures may be shown. While loading, before the first successful run, and after a
 * failed documents listener this is false, so a page reads exactly as it did before the figures existed.
 * A false result does not mean `meta` is null (the docs may be unloaded or no run may have succeeded), so do not read `meta` there.
 */
export function successUpdatesReady(meta: SuccessUpdatesMeta | null, docsLoaded: boolean): meta is SuccessUpdatesMeta {
  return meta !== null && meta.last_success_at !== null && docsLoaded;
}

/** "15 overdue · 1 awaiting a first update · 18 active clients" ("1 active client" for one); zeros when there are no counts. */
export function clientUpdatesSummary(counts: SuccessUpdateCounts | null | undefined): string {
  const { overdue, awaiting_first, active } = counts ?? EMPTY_SUCCESS_COUNTS;
  return `${overdue} overdue · ${awaiting_first} awaiting a first update · ${active} active ${active === 1 ? 'client' : 'clients'}`;
}
